import React from 'react';
import { CheckCircle, BarChart3, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from '../SEO';

const AdmissionsEngine: React.FC = () => {
    const navigate = useNavigate();

    const handleActionClick = () => {
        navigate('/eligibility?service=admissions');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20">
            <SEO
                title="Predictive Admissions Engine | NexOverseas"
                description="Secure your university offer with transparency. Our proprietary engine forecasts your exact likelihood of admission using historical data and machine learning."
                canonical="/admissions/admissions-engine"
            />

            <div className="bg-[#009B8F] text-white py-16 mb-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <span className="text-white/80 font-bold tracking-widest text-xs uppercase mb-4 block">Admissions Suite</span>
                    <h1 className="text-4xl md:text-5xl font-serif mb-4">Predictive Admissions Engine</h1>
                    <p className="text-xl text-white/90 max-w-2xl leading-relaxed">
                        Forecasting your exact likelihood of admission through machine learning and historical data.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                    <div className="prose prose-lg text-slate-600 max-w-none">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 bg-[#009B8F]/10 rounded-full">
                                <BarChart3 className="text-[#009B8F] w-8 h-8" />
                            </div>
                            <h2 className="text-3xl font-serif text-[#011E26] m-0">No More Guesswork</h2>
                        </div>

                        <p className="leading-relaxed mb-6">
                            Securing a university offer shouldn't be a guessing game. Traditional applications leave
                            students uncertain and institutions overwhelmed. At NexOverseas, we bring transparency to
                            the process with our proprietary Predictive Admissions Engine.
                        </p>

                        <p className="leading-relaxed mb-6">
                            By leveraging historical data, statistical algorithms, and machine learning, our platform
                            forecasts your exact likelihood of admission. Rather than relying on manual reporting or
                            human instinct, the engine interprets trends across thousands of global applications to
                            provide a highly accurate, personalized probability score.
                        </p>

                        <p className="leading-relaxed mb-6 border-l-4 border-[#009B8F] pl-6 italic bg-slate-50 py-4">
                            The system continuously cross-references your academic profile, test scores, and financial
                            background against real-time university entry requirements and shifting demographic quotas.
                        </p>

                        <p className="leading-relaxed mb-8 text-[#011E26] font-medium">
                            This allows you to focus your time, energy, and application fees solely on the institutions
                            where you have the highest mathematical probability of success, while simultaneously
                            providing our B2B university partners with pre-vetted, high-yield applicants.
                        </p>

                        <div className="bg-slate-50 p-8 rounded-sm border border-slate-100 mb-8 shadow-sm">
                            <h3 className="text-lg font-bold text-[#011E26] mb-6 uppercase tracking-widest">Our Predictive Admissions Features Include:</h3>
                            <ul className="space-y-4">
                                {[
                                    "Instant probability scoring based on historical university acceptance data and machine learning algorithms.",
                                    "Real-time API feeds syncing directly with current university entry requirements and deadlines.",
                                    "Automated analysis of country-specific intake quotas and institutional diversity targets.",
                                    "Trend forecasting to predict upcoming changes in minimum entry thresholds.",
                                    "Data-backed shortlisting to eliminate wasted application fees and optimize student success."
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
                                        <CheckCircle className="w-5 h-5 text-[#009B8F] flex-shrink-0 mt-0.5" />
                                        <span className="font-medium text-slate-700">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <button
                            onClick={handleActionClick}
                            className="group inline-flex items-center gap-3 bg-[#011E26] text-white px-8 py-4 font-bold uppercase tracking-widest text-xs hover:bg-[#009B8F] transition-all duration-300 rounded-sm shadow-xl"
                        >
                            Run a check with AI <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="space-y-8">
                        <div className="relative rounded-sm overflow-hidden shadow-2xl">
                            <img
                                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800"
                                alt="Data visualization and analytics"
                                className="w-full h-[400px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#011E26]/80 to-transparent flex flex-col justify-end p-8">
                                <div className="bg-white/10 backdrop-blur-md p-4 rounded border border-white/20 inline-block mb-4">
                                    <span className="text-[#009B8F] text-2xl font-bold">94%</span>
                                    <p className="text-white text-xs uppercase tracking-widest">Prediction Accuracy</p>
                                </div>
                                <p className="text-white font-serif italic text-lg">"The probability score allowed me to apply with absolute confidence."</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <img
                                src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&q=80&w=400"
                                alt="Tech analytics"
                                className="rounded-sm shadow-lg h-48 w-full object-cover"
                            />
                            <img
                                src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&q=80&w=400"
                                alt="Success celebration"
                                className="rounded-sm shadow-lg h-48 w-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdmissionsEngine;
